import { SegmentGroup as ChakraSegmentGroup, Box, Tooltip } from '@chakra-ui/react';
import * as React from 'react';

export interface ISegmentItem {
  value: string;
  label: React.ReactNode;
  tooltip?: string;
  'data-testid'?: string;
}

interface IProps {
  value?: string;
  items: ISegmentItem[];
  onValueChange: (details: { value: string }) => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'fullWidth';
  style?: React.CSSProperties;
  showIndicator?: boolean;
}

/**
 * Custom SegmentGroup component that wraps Chakra's SegmentGroup
 * with consistent styling across the application.
 *
 * Variants:
 * - default: Compact styling for navigation menus (PageHeader)
 * - fullWidth: Fills container width, items have equal flex (ReportMenu)
 */
export const SegmentGroup = (props: IProps): React.ReactElement => {
  const {
    value,
    items,
    onValueChange,
    size = 'md',
    variant = 'default',
    style,
    showIndicator = true,
  } = props;

  const isFullWidth = variant === 'fullWidth';

  return (
    <Box
      style={{
        ...style,
        ...(isFullWidth && {
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }),
      }}
      css={{
        // Root styles
        '& [data-part="root"]': {
          background: 'menuBackground',
          borderRadius: '6px',
          display: 'flex',
          ...(isFullWidth && {
            width: '100%',
            maxWidth: '100%',
            minWidth: '100%',
            overflow: 'hidden',
          }),
        },
        // Item styles
        '& [data-part="item"]': {
          justifyContent: 'center',
          color: 'buttonText',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          position: 'relative',
          ...(isFullWidth && {
            flex: '1 1 auto',
            padding: '6px 10px',
          }),
        },
        // Vertical separator lines between items
        '& [data-part="item"]:not(:first-of-type)::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '1px',
          height: '60%',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          zIndex: 0,
        },
        // Hide separator when item or previous sibling is checked (only when showing indicator)
        ...(showIndicator && {
          '& [data-part="item"][data-state="checked"]::before, & [data-part="item"][data-state="checked"] + [data-part="item"]::before':
            {
              opacity: 0,
            },
        }),
        // Item text styles
        '& [data-part="item-text"]': {
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        // Indicator styles
        '& [data-part="indicator"]': {
          background: 'primary',
        },
        // Checked state (only when showing indicator)
        ...(showIndicator && {
          '& [data-part="item"][data-state="checked"]': {
            color: 'buttonText',
          },
          '& [data-part="item"][data-state="checked"] i': {
            color: 'warningAccent',
          },
        }),
        // Icon highlight on hover
        '& [data-part="item"]:hover i': {
          color: 'warningAccent',
        },
      }}
    >
      <ChakraSegmentGroup.Root
        value={value}
        onValueChange={onValueChange}
        size={size}
        width={isFullWidth ? '100%' : undefined}
        minWidth={isFullWidth ? '100%' : undefined}
      >
        {showIndicator && <ChakraSegmentGroup.Indicator />}
        {items.map(
          (item: ISegmentItem): React.ReactElement =>
            item.tooltip ? (
              <Tooltip.Root
                key={item.value}
                positioning={{ placement: 'bottom' }}
                openDelay={300}
              >
                <Tooltip.Trigger asChild>
                  <ChakraSegmentGroup.Item
                    value={item.value}
                    data-testid={item['data-testid']}
                  >
                    <ChakraSegmentGroup.ItemText>
                      {item.label}
                    </ChakraSegmentGroup.ItemText>
                    <ChakraSegmentGroup.ItemHiddenInput />
                  </ChakraSegmentGroup.Item>
                </Tooltip.Trigger>
                <Tooltip.Content>{item.tooltip}</Tooltip.Content>
              </Tooltip.Root>
            ) : (
              <ChakraSegmentGroup.Item
                key={item.value}
                value={item.value}
                data-testid={item['data-testid']}
              >
                <ChakraSegmentGroup.ItemText>
                  {item.label}
                </ChakraSegmentGroup.ItemText>
                <ChakraSegmentGroup.ItemHiddenInput />
              </ChakraSegmentGroup.Item>
            )
        )}
      </ChakraSegmentGroup.Root>
    </Box>
  );
};

export default SegmentGroup;
